// TarjetasIconoSection.js — Sección `tarjetas_icono`: tarjetas blancas con icono
// en caja de color, título, texto y enlace opcional (los «pilares» de LAAC).
//
// Cada tarjeta: { icono (nombre del catálogo lucide) | imagen, color_icono,
// fondo_icono, titulo, texto (admite <strong>), enlace_texto, enlace_url }.
import React from 'react';
import styled, { css, keyframes } from 'styled-components';
import { Link } from 'react-router-dom';
import { BACKEND_BASE } from '../../../services/api';
import IconoLucide from '../iconosLucide';

const urlImagen = (v) => (!v ? null : (/^https?:\/\//.test(v) || v.startsWith('/') ? v : `${BACKEND_BASE}/${v}`));

const subir = keyframes`from { opacity: 0; transform: translate3d(0, 30px, 0); } to { opacity: 1; transform: none; }`;

const Seccion = styled.section`
  background: ${p => p.$fondo};
  padding: ${p => p.$padding};
  @media (max-width: 980px) { padding: ${p => p.$paddingMovil}; }
`;

const Fila = styled.div`
  width: 80%;
  max-width: ${p => p.$ancho};
  margin: 0 auto;
  font-family: Arial, Helvetica, sans-serif;
`;

const Encabezado = styled.div`
  text-align: ${p => p.$align};
  margin-bottom: 30px;
  h2 { margin: 0 0 8px; padding: 0; font-family: 'Roboto', Helvetica, Arial, sans-serif; font-size: 40px; font-weight: 700; line-height: 1.05em; color: ${p => p.$color}; }
  p { margin: 0; color: #808080; font-size: 18px; }
  @media (max-width: 980px) { text-align: center; h2 { font-size: 26px; } }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(${p => p.$columnas}, minmax(0, 1fr));
  gap: ${p => p.$gap};
  @media (max-width: 980px) { grid-template-columns: 1fr; gap: 30px; }
`;

const Tarjeta = styled.div`
  background: #fff;
  border: 1px solid #eae5f6;
  border-radius: 14px;
  padding: 20px;
  box-sizing: border-box;
  /* Alto fijo opcional (config.alto_tarjeta), como las tarjetas de laacacademy.com/curso. */
  ${p => p.$alto && `min-height: ${p.$alto};`}
  transition: box-shadow .3s ease;
  ${p => p.$animar && css`
    animation: ${subir} 700ms ease both;
    animation-delay: ${p.$retraso}ms;
    @media (prefers-reduced-motion: reduce) { animation: none; }
  `}
  &:hover { box-shadow: 0 25px 50px -12px rgb(0 0 0 / .25); }
  &:hover .icono { transform: scale(1.2); }
  h3 { margin: 0 0 7px; padding: 0; font-family: 'Roboto', Helvetica, Arial, sans-serif; font-size: 21px; font-weight: 700; line-height: 1em; color: #1c0b31; }
  .texto { color: #808080; font-size: 15px; line-height: 1.3em; margin: 0 0 15px; }
  .texto strong { color: #260e45; }
`;

const CajaIcono = styled.div`
  width: 41px;
  height: 41px;
  border-radius: 7px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 19px;
  background: ${p => p.$fondo};
  color: ${p => p.$color};
  transition: transform 1s ease;
  img { width: 25px; height: 25px; object-fit: contain; }
`;

const enlaceCss = css`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 14px;
  color: #7c6b8a;
  text-decoration: none;
  transition: color .3s ease;
  &:hover { color: #7b2fbe; }
`;
const EnlaceInterno = styled(Link)`${enlaceCss}`;
const EnlaceExterno = styled.a`${enlaceCss}`;

const TarjetasIconoSection = ({ config = {}, tl = (v, d = '') => v || d }) => {
  const c = config;
  const items = Array.isArray(c.items) ? c.items.filter(it => it && (it.titulo || it.texto)) : [];
  if (!items.length) return null;
  const titulo = tl(c.titulo, '');
  const subtitulo = tl(c.subtitulo, '');

  return (
    <Seccion
      $fondo={c.fondo || 'linear-gradient(180deg, #efeaf8 0%, #f8f5ff 99%)'}
      $padding={c.padding || '67px 0 79px'}
      $paddingMovil={c.padding_movil || '40px 0 50px'}
    >
      <Fila $ancho={c.ancho_maximo || '1200px'}>
        {(titulo || subtitulo) && (
          <Encabezado $align={c.alineacion_titulo || 'left'} $color={c.color_titulo || '#1a0a2e'}>
            {titulo && <h2>{titulo}</h2>}
            {subtitulo && <p>{subtitulo}</p>}
          </Encabezado>
        )}
        <Grid $columnas={Math.min(Number(c.columnas) || items.length, 4)} $gap={c.separacion || '5.5%'}>
          {items.map((it, i) => {
            const img = urlImagen(it.imagen);
            const enlace = it.enlace_url && tl(it.enlace_texto, 'Explorar');
            const externo = /^https?:\/\//i.test(it.enlace_url || '');
            return (
              <Tarjeta key={it.id || i} $animar={c.animar !== false} $retraso={i * 120} $alto={c.alto_tarjeta || null}>
                <CajaIcono className="icono" $fondo={it.fondo_icono || '#ede9fe'} $color={it.color_icono || '#7b2fbe'}>
                  {img ? <img src={img} alt="" /> : <IconoLucide nombre={it.icono || 'estrella'} size={25} />}
                </CajaIcono>
                <h3>{tl(it.titulo, '')}</h3>
                {it.texto && <div className="texto" dangerouslySetInnerHTML={{ __html: tl(it.texto, '') }} />}
                {enlace && (externo
                  ? <EnlaceExterno href={it.enlace_url} target={it.nueva_pestana ? '_blank' : undefined} rel="noopener noreferrer">{enlace}<IconoLucide nombre="flecha" size={17} /></EnlaceExterno>
                  : <EnlaceInterno to={it.enlace_url}>{enlace}<IconoLucide nombre="flecha" size={17} /></EnlaceInterno>)}
              </Tarjeta>
            );
          })}
        </Grid>
      </Fila>
    </Seccion>
  );
};

export default TarjetasIconoSection;
